import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-for-freelancers',
  title: 'Telegram Stars for Freelancers: Invoice a Client Without a Bank Transfer',
  metaTitle: 'Telegram Stars for Freelancers and Coaches',
  metaDescription:
    'How freelancers and coaches invoice a client in Telegram Stars: single-use links, deposits, file delivery, and when a bank transfer is better.',
  keywords: [
    'telegram stars invoice',
    'freelance telegram stars',
    'invoice client telegram',
    'coaching payment telegram stars',
    'telegram stars for freelancers',
    'get paid on telegram',
  ],
  category: 'Business',
  datePublished: '2026-10-22',
  dateModified: '2026-10-22',
  readingTimeMin: 7,
  excerpt:
    'If the client already lives in Telegram, a Stars invoice is shorter than sending bank details. A single-use link closes after they pay. A content sale can attach the deliverable. Here is when that is the right invoice, and when it is not.',
  keyTakeaways: [
    'Use a single payment link when one client should pay once. The link closes after that payment.',
    'Use a content sale when the deliverable is a file or an HTTPS link that should arrive in Telegram automatically.',
    'You are paid in Stars, then in TON after 21 days, not in fiat on your bank account. Say that before the client pays.',
    'A 3% fee and a 100 Star minimum payout on Telegram Stars Payment Platform fit small invoices; very large retainers may still belong on a bank transfer.',
  ],
  body: [
    h2('When a Stars invoice is the right tool'),
    p(
      'Stars fit work that is agreed in a Telegram chat: a design critique, a 45-minute call, a short audit, a voiceover, a one-page brief. The client does not create an account on your site. They open a link, see the title and the amount, and confirm with the Stars they already have or a top-up Telegram offers on the spot.',
    ),
    p(
      'Stars are a poor fit when the client needs a VAT invoice in euros, when you need the money in a bank account this week, or when the amount is large enough that a 21-day hold is a cash-flow problem. In those cases, take a Stars deposit for the kickoff and the balance by bank transfer. Be explicit about which part is which.',
    ),

    h2('Three invoice shapes'),
    table(
      ['Shape', 'Closes after payment', 'Sends the work'],
      [
        ['Single payment', 'Yes', 'No. You deliver yourself.'],
        ['Content sale, single use', 'Yes, if you turn that on', 'Yes: file, photo, video, audio, HTTPS link or message'],
        ['Reusable payment', 'No', 'No. One link, many clients, same price.'],
      ],
    ),
    ul([
      '**Single payment** for a custom quote. Put the client’s name or the job in the title so your ledger stays readable: “Maya — homepage audit”.',
      '**Content sale** when the job is the file. A logo pack, a PDF audit, a preset. See [automatic file delivery](/blog/deliver-files-with-telegram-stars).',
      '**Reusable payment** for a fixed menu: “30-min call — 1,000 Stars”. Anyone with the link can book by paying.',
    ]),

    h2('Write the sheet so the client is not surprised'),
    ol([
      'Title: the job, in their words. “Homepage audit”, not “Invoice #104”.',
      'Description: what they get, when, and that the charge is in Stars. Example: “PDF audit within 3 days. Paid in Telegram Stars. Payout to me is in TON, which does not change your price.”',
      'Amount: convert your usual price with the current Star rate, then round to a pack-friendly number (250, 500, 1,000, 2,500). The method is in [how to price a product in Stars](/blog/how-to-price-products-in-telegram-stars).',
    ]),
    callout(
      'Agree the scope in the chat before you send the link. The payment sheet is a checkout, not a contract. A one-line description will not protect you if the job was never defined.',
    ),

    h2('Deposits and the second half'),
    p(
      'A practical split for a longer job: 50% as a single-use Stars link to start, 50% as a second single-use link when you send the draft. Each payment has its own 21-day hold, counted from the day that payment arrives, not from the start of the project. If you need the full amount in a bank account before you start, do not use Stars for the whole fee.',
    ),

    h2('What lands in your account'),
    p(
      'The client’s Stars are credited to your platform balance immediately, with a ledger line for that link. You can withdraw to a TON wallet from **100 Stars** after **21 days**. The platform fee is **3%**, taken at withdrawal. There is no monthly fee. Fragment’s own minimum is higher (1,000 Stars); the platform minimum is the one that matters if you cash out there.',
    ),
    p('Compare this with card and crypto checkouts in [Telegram Stars vs Stripe, PayPal and crypto](/blog/telegram-stars-vs-stripe-paypal-crypto).'),

    h2('A simple habit that saves disputes'),
    ul([
      'One link per job. Do not reuse a single-use link, and do not send a reusable “call” link for a custom quote.',
      'After they pay, reply in the same chat with the delivery date. The Stars receipt is not a message from you.',
      'If you deliver a file through a content sale, still send one human message. Automatic delivery confirms the file; it does not confirm you understood the brief.',
    ]),
  ],
  faq: [
    {
      q: 'Can my client pay by card instead of Stars?',
      a: 'On this checkout, no. They pay in Stars. If they do not have enough, Telegram lets them buy a pack in the same sheet and then completes the payment.',
    },
    {
      q: 'Will I receive euros?',
      a: 'No. You receive Stars, then TON when you withdraw. Convert TON yourself if you need fiat. Tell the client the price in Stars before they open the link.',
    },
    {
      q: 'Can I send a different price to each client?',
      a: 'Yes. Create a single-payment invoice per quote. The link closes after that client pays, so the next client cannot use the same URL.',
    },
    {
      q: 'Is this legal for my freelance business?',
      a: 'Stars are a payment rail, not a tax filing. You still record the income in your own bookkeeping. If a client requires a local VAT invoice, issue that document separately or use a fiat method.',
    },
  ],
  relatedSlugs: [
    'deliver-files-with-telegram-stars',
    'how-to-price-products-in-telegram-stars',
    'telegram-stars-fees-explained',
    'withdraw-telegram-stars-to-ton',
  ],
};

export default article;
